import React, { useContext, useEffect, useState } from "react";
import { useQuery, useMutation } from "react-apollo";
import PropTypes from "prop-types";
import {
  Card,
  FormLayout,
  Layout,
  List,
  PageActions,
  Select,
  TextField,
  TextContainer,
} from "@shopify/polaris";

// import context
import { PrivateContext } from "lib/context";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import components
import { Banner, Sheet } from "lib/components";
import OnboardingStepSkeleton from "app/onboarding/modules/operator/stepperOnboarding/features/components/onboardingStepSkeleton";

// import constant
import constant from "lib/constant/constant";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// import gql
import { GET_COMMISSION } from "app/onboarding/apollo/queries";
import { UPDATE_COMMISSION } from "app/onboarding/apollo/mutations";

// import yup validation
import LargeModalExample from "app/onboarding/modules/operator/stepperOnboarding/features/components/modal";
import validate from "./yup";

const { onboardingSteps } = constant;

const Commission = (props) => {
  const { setSelectedTab } = props;
  const { currentUser = {}, cms = {}, currentUserRefetch = () => {} } = useContext(PrivateContext);

  const onNext = () => {
    setSelectedTab(onboardingSteps.APP_PERMISSION);
    currentUserRefetch();
  };
  const onPrevious = () => {
    currentUserRefetch();
    setSelectedTab(onboardingSteps.UPDATE_PROFILE);
  };

  const {
    FLAT,
    PERCENTAGE: PERCENTAGE_TEXT,
    SELECTED_STANDARD_TYPE,
    STANDARD_AMOUNT,
    gql: { GET_COMMISSION: GET_COMMISSION_TEXT, UPDATE_COMMISSION: UPDATE_COMMISSION_TEXT },
    symbol: { DOLLAR, PERCENTAGE: PERCENTAGE_SYMBOL },
    value: { MAX_PERCENTAGE, MAX_FLAT, MIN_PRICE },
  } = constant;
  const { _id: currentUserId } = currentUser;

  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });

  const [standardValue, setStandardValue] = useState({
    selectedStandardType: "",
    standardAmount: "",
  });

  const [errorMessage, setErrorMessage] = useState();

  const { loading: queryLoading, data: queryData, error } = useQuery(GET_COMMISSION, {
    variables: { input: { sellerId: currentUserId } },
  });

  const [setCommission, { loading: updateCommissionLoading }] = useMutation(UPDATE_COMMISSION);
  const [sheetActive, setSheetActive] = useState(false);

  useEffect(() => {
    if (!queryData) {
      return;
    }
    const responseData = baseHelper.getResponseData(queryData, GET_COMMISSION_TEXT);
    const responseError = baseHelper.getResponseError(queryData, GET_COMMISSION_TEXT);
    if (responseError) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
      return;
    }
    if (!responseData) {
      return;
    }

    setStandardValue({
      selectedStandardType: responseData.type || "",
      standardAmount: String(responseData.commission) || "",
    });
  }, [queryData, GET_COMMISSION_TEXT]);

  useEffect(() => {
    if (error) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(error) });
    }
  }, [error, cms]);

  if (queryLoading) {
    return <OnboardingStepSkeleton cardCount={1} primaryLines={6} />;
  }

  const valueOptions = [
    {
      label: cms("common.label.flat"),
      value: FLAT,
    },
    {
      label: cms("common.label.percentage"),
      value: PERCENTAGE_TEXT,
    },
  ];

  const handleValidate = async (field, value) => {
    const message = {
      typeRequired: cms("message.error.type"),
      amountRequired: cms("message.error.amount"),
    };
    const validationError = await validate(field, value, message);

    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
  };

  const handleAmountChange = (value) => {
    if (value.length && !baseHelper.validatePositiveNumericValues(value)) {
      return;
    }
    setStandardValue({ ...standardValue, standardAmount: value });
  };

  const handleChange = (value) => {
    setStandardValue({ standardAmount: "", selectedStandardType: value });
  };
  // const standardLists = cms("section.onboardingCommission.optionContent") || [];

  const renderLists = () => {
    return (
      <>
        <List.Item>
          <b>Flat rate: </b>
          A fixed amount per order item (e.g. charge $10 per sale)
        </List.Item>
        <List.Item>
          <b>Percentage: </b>A portion of the total sale price (e.g. charge 20% per sale)
        </List.Item>
      </>
    );
  };

  const dismissBanner = () => setBanner({ isOpen: false, status: "", title: "" });

  const maxAmount = (standardValue.selectedStandardType !== FLAT && MAX_PERCENTAGE) || MAX_FLAT;
  const { moneyFormat = DOLLAR } = currentUser;

  const isSubmitDisabled = !(
    standardValue.selectedStandardType !== "" && parseFloat(standardValue.standardAmount) >= 0
  );

  const saveCommission = async () => {
    const { selectedStandardType = "", standardAmount = "" } = standardValue;

    if (isSubmitDisabled) {
      handleValidate(SELECTED_STANDARD_TYPE, selectedStandardType);
      handleValidate(STANDARD_AMOUNT, standardAmount);
      return;
    }

    if (selectedStandardType === PERCENTAGE_TEXT && standardAmount > MAX_PERCENTAGE) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.percentage"),
      });
      return;
    }
    const requestData = {
      type: selectedStandardType,
      price: parseFloat(standardAmount),
      advancedValues: [],
    };

    try {
      const res = await setCommission({
        variables: { input: requestData },
      });

      const responseData = baseHelper.getResponseData(res.data, UPDATE_COMMISSION_TEXT);

      if (!responseData) {
        const errorResponse = baseHelper.getResponseError(res.data, UPDATE_COMMISSION_TEXT);
        setBanner({ isOpen: true, status: constant.CRITICAL, title: errorResponse });
        return;
      }

      onNext();
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const isAmountDisabled = standardValue.selectedStandardType === "";

  return (
    <Layout id="commission-settings">
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            isOpen={banner.isOpen}
            status={banner.status}
            title={banner.title}
            onDismiss={() => dismissBanner()}
          />
        </Layout.Section>
      )}
      <Layout.Section>
        <Card
          title="Commission Settings"
          // actions={[{ content: "Learn more",onAction: () => setSheetActive(!sheetActive) }]}
        >
          <Card.Section>
            {/* <TextContainer>{cms("section.onboardingCommission.subDescription")}</TextContainer> */}
            <TextContainer>Define how much you will earn from each sale.</TextContainer>
            <br />
            {/* <Stack spacing="loose" vertical> */}
            <FormLayout>
              <FormLayout.Group>
                <Select
                  id="standardType"
                  placeholder={cms("section.onboardingCommission.placeHolder.type")}
                  label={`${cms("section.onboardingCommission.label.type")}*`}
                  value={standardValue.selectedStandardType}
                  options={valueOptions}
                  onChange={handleChange}
                  onBlur={() => handleValidate(SELECTED_STANDARD_TYPE, standardValue.selectedStandardType)}
                  error={errorMessage && errorMessage.selectedStandardType}
                />
                <TextField
                  id="standardAmount"
                  placeholder={cms("section.onboardingCommission.placeHolder.value")}
                  label={`${cms("section.onboardingCommission.label.value")}*`}
                  value={standardValue.standardAmount}
                  min={MIN_PRICE}
                  max={maxAmount}
                  prefix={(!isAmountDisabled && standardValue.selectedStandardType === FLAT && moneyFormat) || ""}
                  suffix={(!isAmountDisabled && standardValue.selectedStandardType !== FLAT && PERCENTAGE_SYMBOL) || ""}
                  onChange={(value) => handleAmountChange(value)}
                  onBlur={() => handleValidate(STANDARD_AMOUNT, standardValue.standardAmount)}
                  error={errorMessage && errorMessage.standardAmount}
                  disabled={isAmountDisabled}
                />
              </FormLayout.Group>
            </FormLayout>
            <br />
            <Banner isOpen status="info">
              {/* <p>{cms("section.onboardingCommission.label.options")}</p> */}
             <p>Understand your options:</p>
              <br />
              <p>
                <List type="bullet">{renderLists()}</List>
              </p>
            </Banner>
          </Card.Section>
        </Card>
        <PageActions
          primaryAction={{
            id: "MC_PORTAL_ONBOARDING_STEP_03",
            content: cms("common.button.nextStep"),
            onAction: () => saveCommission(),
            loading: updateCommissionLoading,
          }}
          secondaryActions={[
            {
              id: "previous",
              content: cms("common.button.previous"),
              onAction: () => onPrevious(),
            },
          ]}
        />
      </Layout.Section>

      <LargeModalExample />
      <Sheet
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        secondaryAction={{ content: cms("common.button.close"), onAction: () => setSheetActive(false) }}
        primaryAction={{ content: cms("common.button.submit"), onAction: () => setSheetActive(false) }}
      />
    </Layout>
  );
};

Commission.propTypes = {
  setSelectedTab: PropTypes.func.isRequired,
};

export default withFeature(withErrorBoundary(Commission), { feature: constant.COMMISSION });
